import { UIElement } from '../ui-element';
import { updateElement } from '../../global/ui-helpers';
import { Component } from '../../decorators/component.decorator';
import { Attribute } from '../../decorators/attribute.decorator';
import { Child } from '../../decorators/child.decorator';
import type { AttributeValue, ObservedAttributes } from '../ui-element.type';
import type { NullOr } from '../../global/utils.type';
import type { UISteps } from './ui-steps';
import styles from './ui-step.css';

/**
 * @class
 * @memberof SharedComponents
 * @augments {UIElement}
 * @alias UIStep
 * @element ui-step
 * @classdesc Represents a class for <code>ui-step</code> element.
 * Draws step.
 * @property {string} label {@attr label} Caption for step.
 * @property {boolean} active {@attr active} Marks step icon as active.
 * @property {boolean} completed {@attr completed} Marks step icon as completed.
 * @property {HTMLDivElement} icon
 * @example
 * <ui-step label="Current step" active="true"></ui-step>
 */
@Component({ tag: 'ui-step', styles })
export class UIStep extends UIElement {
    /**
     * @type {Array<string>}
     */
    static get observedAttributes(): ObservedAttributes {
        return ['active'];
    }

    @Attribute(String) label?: string;
    @Attribute(Boolean) active?: boolean;
    @Attribute(Boolean) completed?: boolean;

    @Child('.ui-step__icon') icon?: HTMLDivElement;

    /**
     * @type {UISteps | null}
     * @readonly
     */
    get container(): NullOr<UISteps> {
        return this.closest<UISteps>('ui-steps');
    }

    /**
     * @inheritDoc
     */
    observeAttributes(
        name: AttributeValue,
        oldValue: AttributeValue,
        newValue: NullOr<string>
    ): void {
        if (this.state !== 'hydrated') {
            return;
        }

        switch (name) {
            case 'active':
                if (this.container) {
                    [].forEach.call(this.container.steps, (step: UIStep) =>
                        step.removeAttribute('aria-current')
                    );
                }
                updateElement(this, {
                    attributes: {
                        'aria-current': 'step',
                    },
                });
                updateElement(this.icon!, {
                    classList: {
                        '-active': !!this.active,
                    },
                });
                break;
            /* istanbul ignore next */
            default:
                break;
        }
    }

    /**
     * @inheritDoc
     */
    override render(): void {
        this.updateElement({
            attributes: {
                'role': 'listitem',
                'aria-current': this.active ? 'step' : null,
            },
            classList: {
                'ui-step__list-item': true,
            },
            children: [
                {
                    tagName: 'div',
                    classList: {
                        'ui-step__icon': true,
                        '-active': !!this.active,
                        '-passed': !!this.completed,
                    },
                    children: [{ tagName: 'ins' }],
                },
                {
                    tagName: 'span',
                    classList: { 'ui-step__label': true },
                    children: [this.label!],
                },
            ],
        });
    }
}
